import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Check, ChevronDown, Clock, Copy, FileImage, FileText, Link2,
  Loader2, Pause, Pencil, Play, RefreshCw, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { compressReceipt, postFormWithRetry } from "@/lib/receipt";
import { useLang } from "@/context/LangContext";

const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
const REDO_STEPS = ["event", "genre", "mood", "lang", "voice", "text", "pay"];
const SOUND_KEYS = ["guitar", "pop", "rnb", "dance", "zastilna", "lightrock", "rap", "deephouse", "disney"];
const SOUND_ICONS = ["🪕", "🎤", "🎷", "💃", "🥂", "🎸", "🎧", "🌊", "🏰"];

const LogoBars = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none" aria-hidden>
    <rect x="2" y="14" width="5" height="12" rx="2.5" fill="#2BA8FF" />
    <rect x="10" y="6" width="5" height="28" rx="2.5" fill="#2BA8FF" />
    <rect x="18" y="2" width="5" height="36" rx="2.5" fill="#FFD400" />
    <rect x="26" y="9" width="5" height="22" rx="2.5" fill="#2BA8FF" />
    <rect x="34" y="15" width="4" height="10" rx="2" fill="#FFD400" />
  </svg>
);

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const safeName = (t) => (t || "track").replace(/[\\/:*?"<>|]/g, "").trim() || "track";

function TrackPlayer({ track, downloadUrl, onShare, hideVariant, badge, onEditTitle }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch(() => {});
    else el.pause();
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !dur) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const t = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * dur;
    el.currentTime = t;
    setCur(t);
  };

  const pct = dur ? (cur / dur) * 100 : 0;

  return (
    <div className="relative rounded-3xl border border-white/12 bg-white/[0.06] backdrop-blur-xl p-4">
      {badge && (
        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 z-10 rounded-full px-3 py-1 text-[10px] font-display font-extrabold shadow-lg"
          style={{ background: "linear-gradient(135deg,#FFD400,#FFB300)", color: "#060A14" }}>
          {badge}
        </span>
      )}
      <audio ref={audioRef} src={track.url} preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => setCur(e.target.currentTime)}
        onLoadedMetadata={(e) => setDur(e.target.duration || 0)}
        onEnded={() => { setPlaying(false); setCur(0); }} />

      <div className="flex items-center gap-3.5">
        <button onClick={toggle} aria-label={playing ? "Pause" : "Play"}
          className="grid place-items-center w-12 h-12 rounded-full grad-tg shrink-0 hover:opacity-90 transition-opacity"
          style={{ boxShadow: "0 10px 30px rgba(43,168,255,0.35)" }}>
          {playing
            ? <Pause size={18} className="text-white" />
            : <Play size={18} className="text-white ml-0.5" />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="min-w-0">
            {!hideVariant && (
              <div className="text-[10px] font-mono uppercase tracking-wider text-[#647089]">
                Варіант {track.idx + 1}
              </div>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-bold truncate min-w-0">{track.title}</div>
              {onEditTitle && (
                <button onClick={() => onEditTitle(track)} aria-label="Редагувати назву" data-testid="track-rename"
                  className="ml-auto grid place-items-center w-8 h-8 rounded-xl border border-white/12 bg-white/[0.05] backdrop-blur-md text-[#9FB0C7] hover:text-white hover:border-[#2BA8FF]/50 shrink-0 transition-colors">
                  <Pencil size={14} />
                </button>
              )}
            </div>
            {track.genre && (
              <div className="text-[11px] text-[#8CA0BC] truncate mt-0.5">{track.genre}</div>
            )}
          </div>

          <div className="mt-2.5 h-1.5 rounded-full bg-white/10 overflow-hidden cursor-pointer" onClick={seek}>
            <div className="h-full bg-[#2BA8FF] rounded-full transition-[width] duration-150"
              style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between text-[10px] font-mono text-[#647089] mt-1 tabular-nums">
            <span>{fmt(cur)}</span><span>{fmt(dur)}</span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <button onClick={onShare}
          className="flex-1 rounded-xl border border-[#2BA8FF]/40 bg-[#2BA8FF]/10 text-[#8CCBFF] text-xs font-bold px-3 py-2 hover:bg-[#2BA8FF]/20 transition-colors">
          Надіслати
        </button>
        <a href={downloadUrl} download
          className="flex-1 text-center rounded-xl border border-white/10 text-[#C6D3E6] text-xs font-bold px-3 py-2 hover:border-white/30 transition-colors">
          Скачати
        </a>
      </div>
    </div>
  );
}

function RedoModal({ code, data, onClose, onRegenStarted, onFreeUsed, onOrderSent }) {
  const { t } = useLang();
  const c = t.create;
  const freeUsed = !!data.regen_free_used;
  const regenPaid = !!data.regen_paid;
  const freeAvailable = regenPaid && !freeUsed;
  const [tab, setTab] = useState(freeAvailable ? "this" : "new");
  const [page, setPage] = useState("event");
  const [cat, setCat] = useState(null);
  const [event, setEvent] = useState(data.draft?.event || "");
  const [eventText, setEventText] = useState(data.draft?.event_text || "");
  const [genre, setGenre] = useState(data.draft?.genre || "");
  const [mood, setMood] = useState(data.draft?.mood || "");
  const [lang, setLang] = useState(data.draft?.song_language || "uk");
  const [voice, setVoice] = useState(data.draft?.voice || "female");
  const [desc, setDesc] = useState(data.draft?.facts || "");
  const [textReady, setTextReady] = useState(false);
  const [oldText, setOldText] = useState(false);
  const [rewriteDesc, setRewriteDesc] = useState(data.draft?.facts || "");
  const [remaining, setRemaining] = useState(data.text_gen_remaining || { this: 2, new: 5 });
  const [lyrics, setLyrics] = useState(data.lyrics || "");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [uploadPct, setUploadPct] = useState(null);
  const [rewriteOpen, setRewriteOpen] = useState(false);
  const [card, setCard] = useState("");
  const [cardLoading, setCardLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [preview, setPreview] = useState("");
  const [orderNumber, setOrderNumber] = useState(null);
  const fileRef = useRef(null);
  const advanceRef = useRef(null);

  const later = (fn) => {
    clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(fn, 200);
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape" && busy !== "submit") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [busy, onClose]);

  useEffect(() => {
    if (tab !== "new" || cat) return;
    api.get("/public/constructor")
      .then((r) => setCat(r.data || {}))
      .catch(() => setCat({}));
  }, [tab, cat]);

  const cardTriedRef = useRef(false);
  useEffect(() => {
    // Одна попытка на открытие модалки: при сбое/пустой карте эффект больше не
    // перезапускается (раньше это давало бесконечный цикл запросов и фриз).
    if (page !== "pay" || card || cardTriedRef.current) return;
    cardTriedRef.current = true;
    setCardLoading(true);
    api.get("/public/payment-card")
      .then((r) => setCard(r.data?.card || ""))
      .catch(() => setCard(""))
      .finally(() => setCardLoading(false));
  }, [page, card]);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  useEffect(() => () => clearTimeout(advanceRef.current), []);

  const events = cat?.events || [];
  const genres = [...(cat?.genres_main || []), ...(cat?.genres_extra || [])];
  const moods = cat?.moods || [];
  const langs = cat?.langs || [];
  const voices = cat?.voices || [];

  const stepIdx = REDO_STEPS.indexOf(page);
  const canNext =
    (page === "event" && events.some((x) => x.key === event)) ||
    (page === "genre" && genres.some((x) => x.key === genre)) ||
    (page === "mood" && moods.some((x) => x.key === mood)) ||
    (page === "lang" && langs.some((x) => x.key === lang)) ||
    (page === "voice" && voices.some((x) => x.key === voice)) ||
    (page === "text" && desc.trim().length >= (textReady ? 40 : 25));

  const nextPage = () => {
    clearTimeout(advanceRef.current);
    setErr("");
    setPage((p) => REDO_STEPS[Math.min(REDO_STEPS.indexOf(p) + 1, REDO_STEPS.length - 1)] || p);
  };

  const backPage = () => {
    clearTimeout(advanceRef.current);
    setErr("");
    setPage((p) => REDO_STEPS[Math.max(REDO_STEPS.indexOf(p) - 1, 0)] || p);
  };

  const pick = (setter, value, from, to) => {
    setter(value);
    setErr("");
    later(() => setPage((p) => (p === from ? to : p)));
  };

  const cardCls = (active) => `relative w-full text-left rounded-2xl border backdrop-blur-md transition-all ${active
    ? "border-[#2BA8FF] bg-[#2BA8FF]/10"
    : "border-white/12 bg-white/[0.05] hover:border-white/30 hover:bg-white/[0.08]"}`;

  const specialCardCls = (active) => `relative w-full text-left rounded-2xl border backdrop-blur-md transition-all ${active
    ? "border-[#2BA8FF] bg-[#2BA8FF]/20"
    : "border-[#2BA8FF]/35 bg-[#2BA8FF]/10 hover:border-[#2BA8FF]/60 hover:bg-[#2BA8FF]/15"}`;

  const ONE_LINE = "block whitespace-nowrap overflow-hidden pr-6";

  const startRegen = async (text) => {
    if (busy) return;
    setBusy("regen");
    setErr("");
    try {
      await api.post(`/public/share/${code}/regen`, { lyrics: (text || "").trim() || null });
      onRegenStarted();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 400 && detail === "free-used") {
        onFreeUsed();
        setTab("new");
      } else if (status === 409 || detail === "generating") {
        setErr("Зараз уже генерується новий варіант. Результат з'явиться на сторінці — спробуйте трохи пізніше.");
        toast.error("Пісня вже генерується — трохи зачекайте");
      } else if (detail === "lyrics-too-short") {
        setErr("Текст занадто короткий — потрібно щонайменше 40 символів.");
        toast.error("Текст занадто короткий — мінімум 40 символів");
      } else {
        setErr("Не вдалося запустити перегенерацію. Спробуйте ще раз.");
        toast.error("Не вдалося запустити перегенерацію");
      }
    } finally { setBusy(""); setUploadPct(null); }
  };

  const rewriteText = async () => {
    if (busy) return;
    const d = data.draft || {};
    setBusy("rewrite");
    setErr("");
    try {
      const r = await api.post(`/public/share/${code}/draft-lyrics`, {
        context: "this",
        event: d.event || "nooccasion",
        event_text: d.event_text || "",
        genre: d.genre || "pop",
        mood: d.mood || "",
        song_language: d.song_language || "uk",
        voice: d.voice || "female",
        facts: rewriteDesc.trim() || d.facts || "",
      });
      setLyrics(r.data?.lyrics || "");
      if (typeof r.data?.remaining === "number") {
        setRemaining((v) => ({ ...v, this: r.data.remaining }));
      }
      setRewriteOpen(false);
      toast.success("Новий текст готовий — перевірте та натисніть «Почати»");
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "text-limit") {
        setErr("Ліміт генерацій тексту вичерпано (2). Текст можна редагувати вручну.");
        toast.error("Ліміт генерацій тексту вичерпано");
      } else if (detail === "facts-too-short") {
        setErr("Опис занадто короткий — додайте трохи деталей.");
      } else {
        setErr("Не вдалося переписати текст. Спробуйте ще раз.");
        toast.error("Не вдалося переписати текст");
      }
    } finally { setBusy(""); setUploadPct(null); }
  };

  const genText = async () => {
    if (busy) return;
    if (desc.trim().length < 15) {
      setErr("Додайте трохи більше деталей про історію — мінімум 15 символів.");
      return;
    }
    setBusy("gen-text");
    setErr("");
    try {
      const r = await api.post(`/public/share/${code}/draft-lyrics`, {
        context: "new",
        event: event || "nooccasion",
        event_text: eventText || "",
        genre: genre || "pop",
        mood: mood || "",
        song_language: lang || "uk",
        voice: voice || "female",
        facts: desc.trim(),
      });
      setDesc(r.data?.lyrics || "");
      setTextReady(true);
      if (typeof r.data?.remaining === "number") {
        setRemaining((v) => ({ ...v, new: r.data.remaining }));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "text-limit") {
        setErr("Ліміт генерацій тексту вичерпано (5). Текст можна написати вручну.");
        toast.error("Ліміт генерацій тексту вичерпано");
      } else if (detail === "facts-too-short") {
        setErr("Додайте трохи більше деталей про історію — мінімум 15 символів.");
        toast.error("Додайте більше деталей — мінімум 15 символів");
      } else {
        setErr("Не вдалося згенерувати текст. Спробуйте ще раз або напишіть вручну.");
        toast.error("Не вдалося згенерувати текст");
      }
    } finally { setBusy(""); setUploadPct(null); }
  };

  const pickReceipt = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!(f.type.startsWith("image/") || f.type === "application/pdf")
        || f.size > MAX_RECEIPT_BYTES) {
      setErr("Чек має бути фото (jpg, png, webp) або PDF до 10 МБ.");
      return;
    }
    if (preview) URL.revokeObjectURL(preview);
    setReceipt(f);
    setPreview(URL.createObjectURL(f));
    setErr("");
    const small = await compressReceipt(f);
    if (small !== f) setReceipt(small);
  };

  const copyCard = async () => {
    try {
      await navigator.clipboard.writeText(card);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard unavailable */ }
  };

  const submit = async () => {
    if (busy || !receipt) return;
    const minLen = textReady ? 40 : 25;
    if (desc.trim().length < minLen) {
      setErr(textReady
        ? "Готовий текст занадто короткий — мінімум 40 символів."
        : "Опишіть пісню детальніше — мінімум 25 символів.");
      return;
    }
    setBusy("submit");
    setUploadPct(0);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("genre", genre);
      fd.append("voice", voice);
      fd.append("description", desc.trim());
      fd.append("event", event || "");
      fd.append("event_text", eventText || "");
      fd.append("mood", mood || "");
      fd.append("song_language", lang || "");
      fd.append("ready_lyrics", textReady ? desc.trim() : "");
      fd.append("receipt", receipt);
      const r = await postFormWithRetry(`/public/share/${code}/new-song`, fd, { onProgress: setUploadPct });
      setOrderNumber(r.data?.number);
      setPage("done");
      onOrderSent();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "description-too-short") {
        setErr("Опишіть пісню детальніше — мінімум 25 символів.");
        toast.error("Опишіть пісню детальніше — мінімум 25 символів");
      } else if (detail === "receipt-invalid") {
        setErr("Чек має бути фото (jpg, png, webp) або PDF до 10 МБ.");
        toast.error("Перевірте формат чека — jpg, png, webp або PDF до 10 МБ");
      } else {
        setErr("Не вдалося надіслати заявку. Перевірте зв'язок і спробуйте ще раз — або надішліть чек у підтримку через бота.");
        toast.error("Не вдалося надіслати заявку");
      }
    } finally { setBusy(""); setUploadPct(null); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[90] bg-[#060A14] overflow-y-auto" data-testid="redo-modal" role="dialog" aria-modal="true">
      <div className="pointer-events-none fixed inset-0 hero-flag opacity-70" />
      <div className="sticky top-0 z-10 bg-[#060A14]/85 backdrop-blur"
        style={{ paddingTop: "max(10px, env(safe-area-inset-top))" }}>
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            {tab === "new" && page !== "event" && page !== "done" && (
              <button data-testid="redo-back" onClick={backPage} aria-label="Назад"
                className="grid place-items-center w-8 h-8 rounded-full border border-white/10 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors shrink-0">
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-[#9FB0C7] font-mono uppercase tracking-wider truncate">
              <RefreshCw size={14} className="text-[#2BA8FF] shrink-0" /> Переробити
            </div>
          </div>
          <button data-testid="redo-close" onClick={onClose} aria-label="Закрити"
            className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="max-w-2xl mx-auto px-5 pb-3">
          <div className="grid grid-cols-2 gap-1 rounded-2xl border border-white/12 bg-white/[0.05] p-1">
            <button data-testid="redo-tab-this" disabled={!freeAvailable}
              onClick={() => { if (freeAvailable) { setTab("this"); setErr(""); } }}
              className={`rounded-xl px-3 py-2 text-sm font-bold transition-colors ${tab === "this"
                ? "grad-tg text-white"
                : !freeAvailable ? "text-[#647089] opacity-50 cursor-not-allowed" : "text-[#9FB0C7] hover:text-white"}`}>
              Ця ж пісня
            </button>
            <button data-testid="redo-tab-new"
              onClick={() => { setTab("new"); setErr(""); }}
              className={`relative rounded-xl px-3 py-2 text-sm font-bold transition-colors ${tab === "new" ? "grad-tg text-white" : "text-[#9FB0C7] hover:text-white"}`}>
              Повна заміна
              <span className="absolute -top-2 -right-1.5 rotate-[8deg] rounded-xl px-1.5 py-0.5 text-[10px] font-display font-extrabold shadow-lg"
                style={{ background: "linear-gradient(135deg,#FFD400,#FFB300)", color: "#060A14" }}>
                149 ₴
              </span>
            </button>
          </div>
          {!regenPaid && (
            <p className="mt-2.5 text-center text-xs text-[#9FB0C7] leading-relaxed">
              У цьому замовленні безкоштовної перегенерації немає — доступна платна «Повна заміна».
            </p>
          )}
        </div>

        {tab === "new" && page !== "done" && (
          <div className="max-w-2xl mx-auto px-5 pb-3 flex gap-1.5">
            {REDO_STEPS.map((s, i) => (
              <span key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${i <= stepIdx ? "bg-[#2BA8FF]" : "bg-white/10"}`} />
            ))}
          </div>
        )}
      </div>

      <div className="relative max-w-2xl mx-auto px-5 pt-6 sm:pt-8"
        style={{ paddingBottom: tab === "new" && page !== "done" ? "max(150px, calc(env(safe-area-inset-bottom) + 110px))" : "max(48px, env(safe-area-inset-bottom))" }}>
        {tab === "this" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Ця ж пісня</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">
              Відредагуйте текст — переспіваємо пісню в тому самому стилі. Одна перегенерація безкоштовна.
            </p>

            <div className="mt-6 rounded-3xl border border-white/12 bg-white/[0.05] backdrop-blur-xl p-5">
              <div className="font-bold">Відредагуйте текст пісні</div>
              <p className="text-sm text-[#9FB0C7] mt-1">Просто поправте слова — і натисніть «Почати».</p>
              <textarea data-testid="redo-textarea" value={lyrics} rows={12} maxLength={3000}
                onChange={(e) => { setLyrics(e.target.value); setErr(""); }}
                placeholder="Текст пісні…"
                className="mt-4 w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none p-4 text-sm leading-relaxed resize-none" />
              <div className="flex items-center justify-between text-xs text-[#647089] font-mono mt-2">
                <span>{lyrics.trim().length}/3000</span>
                <span>мін. 40 символів</span>
              </div>
              <button data-testid="redo-start" onClick={() => startRegen(lyrics)}
                disabled={!!busy || lyrics.trim().length < 40}
                className="btn-tg grad-tg mt-3 w-full text-white font-bold px-6 py-3.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                {busy === "regen" ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
                {busy === "regen" ? "Запускаємо…" : "Почати"}
              </button>
              <p className="mt-3 text-center text-xs text-[#647089]">
                Після перегенерації буде 4 варіанти — по 2 з кожної версії.
              </p>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden">
              <button data-testid="redo-rewrite-toggle" onClick={() => setRewriteOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 text-left">
                <span className="text-sm font-semibold text-[#9FB0C7]">Повністю переписати текст</span>
                <ChevronDown size={16} className={`text-[#647089] transition-transform shrink-0 ${rewriteOpen ? "rotate-180 text-[#2BA8FF]" : ""}`} />
              </button>
              {rewriteOpen && (
                <div className="px-4 pb-4 border-t border-white/5">
                  <p className="pt-3 text-xs text-[#9FB0C7] leading-relaxed">
                    Поправте опис за потреби — ШІ напише новий текст за ним. Стиль і музика залишаться ті самі.
                  </p>
                  <textarea data-testid="redo-rewrite-desc" value={rewriteDesc} rows={4} maxLength={3000}
                    onChange={(e) => { setRewriteDesc(e.target.value); setErr(""); }}
                    placeholder="Опис пісні…"
                    className="mt-3 w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none p-3.5 text-sm leading-relaxed resize-none" />
                  <div className="mt-2 flex items-center justify-between text-xs text-[#647089] font-mono">
                    <span>Доступно генерацій тексту: {remaining.this}</span>
                    <span>{rewriteDesc.trim().length}/3000</span>
                  </div>
                  <button data-testid="redo-rewrite" onClick={rewriteText}
                    disabled={!!busy || remaining.this <= 0 || rewriteDesc.trim().length < 15}
                    className="mt-2 w-full rounded-xl border border-white/12 bg-white/[0.05] text-[#C6D3E6] text-sm font-bold px-4 py-3 flex items-center justify-center gap-2 hover:bg-white/[0.08] disabled:opacity-40 transition-colors">
                    {busy === "rewrite" ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
                    {busy === "rewrite" ? "Пишемо текст… до 30 секунд" : "Згенерувати новий текст"}
                  </button>
                </div>
              )}
            </div>

            {err && <p className="mt-4 text-sm text-[#FF8A8A]">{err}</p>}
          </>
        )}

        {tab === "new" && page !== "done" && freeUsed && (
          <div className="mb-5 rounded-2xl border border-[#FFD400]/30 bg-[#FFD400]/10 px-4 py-3 text-sm text-[#FFE066]">
            Безкоштовна перегенерація вже використана
          </div>
        )}

        {tab === "new" && !cat && page !== "text" && page !== "pay" && (
          <div className="mt-6 flex items-center gap-2.5 text-sm text-[#9FB0C7]">
            <Loader2 size={15} className="animate-spin text-[#2BA8FF]" /> Завантажую варіанти…
          </div>
        )}

        {tab === "new" && cat && page === "event" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Привід</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">Для якої події створюємо пісню?</p>
            <div className="mt-5 grid grid-cols-2 lg:grid-cols-3 gap-2.5">
              {events.map((e) => {
                const active = event === e.key;
                const special = e.key === "custom" || e.key === "nooccasion";
                return (
                  <Fragment key={e.key}>
                    <button data-testid={`redo-event-${e.key}`}
                      onClick={() => {
                        setEvent(e.key); setErr("");
                        if (e.key !== "custom") {
                          later(() => setPage((p) => (p === "event" ? "genre" : p)));
                        }
                      }}
                      className={`${special ? specialCardCls(active) : cardCls(active)} p-3.5 text-sm font-semibold`}>
                      <span className={ONE_LINE}>{e.ua || e.en || e.key}</span>
                    </button>
                    {e.key === "custom" && active && (
                      <div className="col-span-2 lg:col-span-3">
                        <input value={eventText} maxLength={200}
                          onChange={(ev) => { setEventText(ev.target.value); setErr(""); }}
                          placeholder="Напр. пісня для сестри"
                          className="w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none px-4 py-3.5 text-base" />
                        <button disabled={eventText.trim().length < 3}
                          onClick={() => later(() => setPage((p) => (p === "event" ? "genre" : p)))}
                          className="btn-tg grad-tg mt-3 w-full text-white font-bold px-6 py-3.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                          Далі <ArrowRight size={17} />
                        </button>
                      </div>
                    )}
                  </Fragment>
                );
              })}
            </div>
          </>
        )}

        {tab === "new" && cat && page === "genre" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Жанр</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">Оберіть стиль музики</p>
            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-2.5">
              {SOUND_KEYS.map((key, i) => {
                const active = genre === key;
                return (
                  <button key={key} data-testid={`redo-genre-${key}`}
                    onClick={() => pick(setGenre, key, "genre", "mood")}
                    className={`${cardCls(active)} px-4 py-3.5 flex gap-3.5 items-start`}>
                    <span className="text-2xl leading-none mt-0.5 shrink-0">{SOUND_ICONS[i]}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-bold text-sm">{c.sounds[i].t}</span>
                      <span className="block text-xs text-[#9FB0C7] mt-1 leading-relaxed">{c.sounds[i].d}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {tab === "new" && cat && page === "mood" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Настрій</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">Які емоції має передати пісня?</p>
            <div className="mt-5 grid grid-cols-2 lg:grid-cols-3 gap-2.5">
              {moods.map((m) => {
                const active = mood === m.key;
                return (
                  <button key={m.key} data-testid={`redo-mood-${m.key}`}
                    onClick={() => pick(setMood, m.key, "mood", "lang")}
                    className={`${cardCls(active)} p-3.5 text-sm font-semibold`}>
                    <span className={ONE_LINE}>{m.ua || m.en || m.key}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {tab === "new" && cat && page === "lang" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Мова</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">Якою мовою співатиме пісня?</p>
            <div className="mt-5 grid grid-cols-3 gap-2.5">
              {langs.map((l) => {
                const active = lang === l.key;
                return (
                  <button key={l.key} data-testid={`redo-lang-${l.key}`}
                    onClick={() => pick(setLang, l.key, "lang", "voice")}
                    className={`${cardCls(active)} p-4 text-sm font-semibold`}>
                    <span className={ONE_LINE}>{l.ua || l.en || l.key}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {tab === "new" && cat && page === "voice" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Голос</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">Хто виконає пісню?</p>
            <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              {voices.map((v) => {
                const active = voice === v.key;
                return (
                  <button key={v.key} data-testid={`redo-voice-${v.key}`}
                    onClick={() => pick(setVoice, v.key, "voice", "text")}
                    className={`${cardCls(active)} p-4 text-sm font-semibold text-center`}>
                    <span className="block whitespace-nowrap overflow-hidden">{v.ua || v.en || v.key}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {tab === "new" && page === "text" && (
          <>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Текст пісні</h2>
            <p className="text-sm text-[#9FB0C7] mt-2">
              Опишіть історію своїми словами — ШІ складе текст, або напишіть готовий.
            </p>
            <div className="mt-4 space-y-2.5">
              <div className={`flex items-center gap-3.5 rounded-2xl border px-4 py-3.5 transition-colors ${textReady ? "border-[#2BA8FF]/50 bg-[#2BA8FF]/10" : "border-white/12 bg-white/[0.05]"}`}>
                <span className="flex-1 min-w-0 text-sm font-bold">У мене є готовий текст</span>
                <button type="button" role="switch" aria-checked={textReady} data-testid="redo-ready-toggle"
                  onClick={() => { setTextReady((v) => { const nv = !v; if (!nv) setOldText(false); return nv; }); setErr(""); }}
                  className={`relative w-11 h-6 rounded-full shrink-0 transition-colors ${textReady ? "bg-[#2F80FF]" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${textReady ? "translate-x-5" : ""}`} />
                </button>
              </div>
              <div className={`flex items-center gap-3.5 rounded-2xl border px-4 py-3.5 transition-colors ${oldText ? "border-[#2BA8FF]/50 bg-[#2BA8FF]/10" : "border-white/12 bg-white/[0.05]"}`}>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold">Використати старий текст</span>
                  <span className="block text-xs text-[#9FB0C7] mt-0.5">Вставимо текст із цього замовлення — можна відредагувати</span>
                </span>
                <button type="button" role="switch" aria-checked={oldText} data-testid="redo-oldtext-toggle"
                  onClick={() => {
                    if (oldText) { setOldText(false); return; }
                    setOldText(true);
                    setTextReady(true);
                    setDesc(data.lyrics || "");
                    setErr("");
                  }}
                  className={`relative w-11 h-6 rounded-full shrink-0 transition-colors ${oldText ? "bg-[#2F80FF]" : "bg-white/15"}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${oldText ? "translate-x-5" : ""}`} />
                </button>
              </div>
            </div>
            <textarea data-testid="redo-desc" value={desc} rows={11} maxLength={3000}
              onChange={(e) => { setDesc(e.target.value); setErr(""); }}
              placeholder={textReady
                ? "[Verse]\nВставте свій текст пісні…"
                : "Наприклад: мамі 55 років, вона любить сад і випічку, найбільша мрія — поїхати до моря…"}
              className="mt-3 w-full rounded-2xl bg-[#0B1220] border border-white/10 focus:border-[#2BA8FF] outline-none p-4 text-sm leading-relaxed resize-none" />
            <div className="flex items-center justify-between text-xs text-[#647089] font-mono mt-2">
              <span>Доступно генерацій тексту: {remaining.new}</span>
              <span>{desc.trim().length}/3000</span>
            </div>
            {textReady ? (
              <p className="mt-2 text-xs text-[#10B981]">
                ✓ Ваш текст піде в пісню дослівно — без переписування ШІ. Мінімум 40 символів.
              </p>
            ) : (
              <button data-testid="redo-gen-text" onClick={genText}
                disabled={!!busy || desc.trim().length < 15 || remaining.new <= 0}
                className="mt-4 w-full rounded-2xl border border-[#2BA8FF]/40 bg-[#2BA8FF]/10 text-[#8CCBFF] font-bold px-6 py-3.5 flex items-center justify-center gap-2 hover:bg-[#2BA8FF]/20 disabled:opacity-40 transition-colors">
                {busy === "gen-text" ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
                {busy === "gen-text" ? "Складаємо текст… до 30 секунд" : "Згенерувати текст"}
              </button>
            )}
          </>
        )}

        {tab === "new" && page === "pay" && (
          <>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold uppercase">Оплата</h2>
              <span className="rotate-[8deg] rounded-xl px-3 py-1 text-base font-display font-extrabold shadow-xl"
                style={{ background: "linear-gradient(135deg,#FFD400,#FFB300)", color: "#060A14" }}>
                149 ₴
              </span>
            </div>
            <p className="text-sm text-[#9FB0C7] mt-2">
              Перекажіть 149 ₴ на картку та додайте фото чека — пісня з'явиться тут після перевірки оплати.
            </p>

            <div className="mt-6 rounded-3xl border border-white/12 bg-white/[0.05] backdrop-blur-xl p-5 space-y-5">
              <div>
                <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">До сплати</div>
                <div className="text-2xl font-display font-extrabold text-[#10B981] mt-1">149 ₴</div>
              </div>
              <div>
                <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">Картка для оплати</div>
                {cardLoading ? (
                  <div className="mt-2 flex items-center gap-2 text-sm text-[#9FB0C7]">
                    <Loader2 size={14} className="animate-spin" /> Завантажую реквізити…
                  </div>
                ) : card ? (
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 min-w-0 rounded-xl bg-black/30 border border-white/10 px-4 py-3 text-base font-mono tracking-wide">{card}</code>
                    <button data-testid="redo-copy-card" onClick={copyCard} title="Копіювати"
                      className="h-11 px-3 rounded-xl border border-white/10 hover:border-[#2BA8FF]/60 transition-colors flex items-center justify-center gap-1.5 text-sm font-bold text-[#C6D3E6] shrink-0 whitespace-nowrap">
                      {copied ? <Check size={16} className="text-[#10B981]" /> : <Copy size={16} />}
                      {copied ? "Готово" : "Копіювати"}
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-[#9FB0C7]">—</div>
                )}
              </div>
              <div>
                <div className="text-[10px] text-[#647089] font-mono uppercase tracking-wider">Фото чека</div>
                <input ref={fileRef} data-testid="redo-receipt" type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={pickReceipt} />
                <button onClick={() => fileRef.current?.click()}
                  className="mt-2 w-full rounded-2xl border border-dashed border-white/20 hover:border-[#2BA8FF]/60 bg-white/[0.02] px-4 py-6 transition-colors">
                  {preview ? (
                    receipt && receipt.type === "application/pdf" ? (
                      <span className="flex flex-col items-center gap-2 text-[#9FB0C7] text-sm">
                        <FileText size={26} className="text-[#2BA8FF]" /> {receipt.name}
                        <span className="text-xs text-[#647089]">PDF · до 10 МБ</span>
                      </span>
                    ) : (
                      <img src={preview} alt="" className="max-h-56 mx-auto rounded-xl" />
                    )
                  ) : (
                    <span className="flex flex-col items-center gap-2 text-[#9FB0C7] text-sm">
                      <FileImage size={26} className="text-[#2BA8FF]" /> Обрати фото чека
                      <span className="text-xs text-[#647089]">jpg, png, webp або PDF · до 10 МБ</span>
                    </span>
                  )}
                </button>
                {preview && (
                  <button onClick={() => fileRef.current?.click()}
                    className="mt-2 text-xs text-[#2BA8FF] hover:underline">
                    Змінити фото
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {tab === "new" && page === "done" && (
          <div className="text-center pt-10">
            <div className="mx-auto grid place-items-center w-20 h-20 rounded-full grad-tg">
              <Check size={38} className="text-white" />
            </div>
            <h2 className="mt-6 font-display text-3xl font-extrabold uppercase">Успішно відправлено ✅</h2>
            <p className="text-[#9FB0C7] mt-3 max-w-md mx-auto leading-relaxed">
              Заявку надіслано на перевірку. Ваша нова пісня з'явиться за цим же
              посиланням разом зі старими піснями — просто оновіть сторінку.
            </p>
            <button data-testid="redo-done-close" onClick={onClose}
              className="btn-tg grad-tg mt-7 text-white font-bold px-8 py-3.5 rounded-2xl">
              Закрити
            </button>
          </div>
        )}

        {tab === "new" && page !== "done" && (
          <div className="sticky bottom-0 z-20 -mx-5 mt-9 px-5 pt-3 bg-[#060A14]/85 backdrop-blur-md border-t border-white/5"
            style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
            {err && <p className="text-sm text-[#FF8A8A] mb-3">{err}</p>}
            <div className="flex items-center gap-3">
              {page !== "event" && (
                <button onClick={backPage}
                  className="rounded-2xl border border-white/10 px-5 py-3.5 font-bold text-sm text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors">
                  Назад
                </button>
              )}
              {page === "pay" ? (
                <button data-testid="redo-submit" onClick={submit}
                  disabled={!!busy || !receipt || desc.trim().length < (textReady ? 40 : 25)}
                  className="font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none"
                  style={{
                    background: "linear-gradient(135deg,#10B981,#059669)",
                    boxShadow: "0 10px 34px rgba(16,185,129,0.35)",
                  }}>
                  {busy === "submit" ? <Loader2 size={17} className="animate-spin" /> : <Check size={17} />}
                  {busy === "submit" ? "Надсилаємо…" : "Надіслати заявку"}
                </button>
              ) : (
                <button data-testid="redo-next" onClick={nextPage} disabled={!canNext || !!busy}
                  className="btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl flex-1 flex items-center justify-center gap-2 disabled:opacity-40 disabled:shadow-none">
                  Далі <ArrowRight size={17} />
                </button>
              )}
              {busy === "submit" && (
                <div className="w-full mt-3" data-testid="upload-progress">
                  <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-300"
                      style={{ width: `${uploadPct || 0}%`, background: "linear-gradient(90deg,#2BA8FF,#10B981)" }} />
                  </div>
                  <div className="mt-1.5 text-center text-xs text-[#9FB0C7]">
                    {uploadPct >= 100 ? "Обробляємо чек…" : `Завантажуємо чек… ${uploadPct || 0}%`}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

export default function SharePlayer({ code: codeProp = "", embedded = false }) {
  const params = useParams();
  const code = codeProp || params.code || "";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [shareTrack, setShareTrack] = useState(null);
  const [busyShare, setBusyShare] = useState("");
  const [redoOpen, setRedoOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pendingRegen, setPendingRegen] = useState(false);

  useEffect(() => {
    api.get(`/public/share/${code}`)
      .then((r) => { setData(r.data); setGenerating(!!r.data.generating); })
      .catch((e) => setErr(e?.response?.data?.detail || "Посилання не знайдено"));
  }, [code]);

  // SSE: статусы и блокировка — мгновенно (опрос остаётся страховкой).
  useEffect(() => {
    let cid = "";
    try { cid = localStorage.getItem("episnya_client_id") || ""; } catch { /* ignore */ }
    const tg = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
    const idata = window.Telegram?.WebApp?.initData || "";
    const qs = new URLSearchParams();
    if (cid) qs.set("client_id", cid);
    if (tg && idata) { qs.set("tg_id", String(tg)); qs.set("init_data", idata); }
    if (![...qs.keys()].length) return undefined;
    let es;
    try { es = new EventSource(`${API}/public/landing/stream?${qs.toString()}`); } catch { return undefined; }
    es.onmessage = (e) => {
      let ev;
      try { ev = JSON.parse(e.data); } catch { return; }
      if (ev?.block) { setBlocked(true); return; }
      api.get(`/public/share/${code}`).then((r) => setData(r.data)).catch(() => {});
    };
    return () => es.close();
  }, [code]);

  const runsHasNew = (d) => (d?.runs || []).some((r) => r.is_new && (r.tracks || []).length > 0);

  const redoActive = !!data?.redo && ["review", "generating"].includes(data.redo.status_group);

  useEffect(() => {
    if (!generating && !pendingRegen && !redoActive) return undefined;
    let stopped = false;
    const startedAt = Date.now();
    const tick = async () => {
      if (stopped) return;
      try {
        const r = await api.get(`/public/share/${code}`);
        if (stopped) return;
        setData(r.data);
        if (r.data.generating) { setGenerating(true); return; }
        if (pendingRegen && !runsHasNew(r.data) && Date.now() - startedAt < 45 * 60 * 1000) return;
        if (runsHasNew(r.data)) toast.success("Новий варіант готовий ✓");
        setGenerating(false);
        setPendingRegen(false);
      } catch { /* retry on next tick */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { stopped = true; clearInterval(id); };
  }, [generating, pendingRegen, redoActive, code]);

  const pageUrl = typeof window !== "undefined"
    ? (embedded && code ? `${window.location.origin}/s/${code}` : window.location.href)
    : "";

  const copyPageLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      toast.success("Посилання скопійовано ✓");
    } catch { toast.error("Не вдалося скопіювати"); }
  };

  const shareDirect = async (t) => {
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: t.title, url: t.url });
        return;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(t.url);
      toast.success("Посилання скопійовано ✓");
    } catch { toast.error(`Скопіюйте вручну: ${t.url}`); }
  };

  const shareFile = async () => {
    if (!shareTrack || busyShare) return;
    setBusyShare("file");
    try {
      const r = await fetch(`${API}/public/share/${code}/tracks/${shareTrack.idx}/download`);
      if (!r.ok) throw new Error("fetch");
      const blob = await r.blob();
      const name = `${safeName(shareTrack.title)}.mp3`;
      const file = new File([blob], name, { type: "audio/mpeg" });
      if (typeof navigator !== "undefined" && navigator.canShare
          && navigator.canShare({ files: [file] })) {
        // Только файл — без title/text, иначе мессенджер шлёт вторым сообщением название
        await navigator.share({ files: [file] });
      } else {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
        toast.success("Файл завантажено — надішліть його вручну");
      }
      setShareTrack(null);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Не вдалося надіслати файл");
    } finally { setBusyShare(""); }
  };

  const shareLink = async () => {
    if (!shareTrack || busyShare) return;
    setBusyShare("link");
    try {
      const r = await api.post(`/public/share/${code}/tracks/${shareTrack.idx}/link`);
      const url = r.data.url;
      let shared = false;
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ title: shareTrack.title, url });
          shared = true;
        } catch (e) {
          if (e?.name === "AbortError") { setShareTrack(null); return; }
        }
      }
      if (!shared) {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Посилання скопійовано ✓");
        } catch { toast.error(`Скопіюйте вручну: ${url}`); }
      }
      setShareTrack(null);
    } catch {
      toast.error("Не вдалося створити посилання");
    } finally { setBusyShare(""); }
  };

  useEffect(() => {
    if (renameTarget) setRenameValue(renameTarget.title || "");
  }, [renameTarget]);

  const saveRename = async () => {
    const t = renameValue.trim();
    if (!t || !renameTarget || renameSaving) return;
    setRenameSaving(true);
    try {
      await api.post(`/public/share/${code}/rename`, { url: renameTarget.url, title: t });
      renameTrack(renameTarget.url, t);
      setRenameTarget(null);
      toast.success("Назву збережено");
    } catch {
      toast.error("Не вдалося зберегти назву");
    } finally { setRenameSaving(false); }
  };

  const renameTrack = (url, title) => {
    setData((d) => {
      if (!d) return d;
      const fix = (t) => (t.url === url ? { ...t, title } : t);
      return {
        ...d,
        tracks: (d.tracks || []).map(fix),
        runs: (d.runs || []).map((r) => ({ ...r, tracks: (r.tracks || []).map(fix) })),
      };
    });
  };

  const runs = data?.runs || [];
  const topUrl = data?.tracks?.[0]?.url || "";
  const topRun = runs.find((r) => (r.tracks?.[0]?.url || "") === topUrl);
  const topBadge = topRun?.is_new ? "Нова" : "";
  const extraRuns = runs.filter((r) => (r.tracks?.[0]?.url || "") !== topUrl);
  const newRuns = extraRuns.filter((r) => r.is_new);
  const prevRuns = extraRuns.filter((r) => !r.is_new);

  return (
    <div className="min-h-screen bg-[#060A14] text-[#F1F5F9] px-5 py-10 relative overflow-hidden">
      <div className="pointer-events-none fixed inset-0 hero-flag opacity-70" />
      <div className="relative max-w-xl mx-auto">
        <a href="/" className="flex items-center justify-center gap-2.5 font-display font-extrabold text-white text-lg mb-6 hover:opacity-90 transition-opacity">
          <LogoBars size={24} /> E-PISNYA
        </a>

        {blocked ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-xl p-10 text-center" data-testid="share-blocked">
            <p className="text-5xl select-none">🔒</p>
            <a href="https://t.me/episnyasupport" target="_blank" rel="noopener noreferrer"
              className="mt-6 inline-block rounded-2xl px-6 py-3 font-bold text-sm text-white"
              style={{ background: "linear-gradient(135deg,#2BA8FF,#1E86E8)" }}>
              💬 Написати в підтримку
            </a>
            <p className="mt-4 text-xs text-[#9FB0C7]">Support: @episnyasupport</p>
          </div>
        ) : err ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-xl p-8 text-center">
            <p className="text-2xl">😔</p>
            <p className="mt-3 text-[#C6D3E6]">{err}</p>
          </div>
        ) : !data ? (
          <div className="rounded-3xl border border-white/10 bg-white/[0.05] backdrop-blur-xl p-8 text-center text-[#9FB0C7]">
            Завантажую пісню…
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="flex justify-center">
                <LogoBars size={56} />
              </div>
              {data.gift ? (
                <>
                  <p className="mt-4 text-sm text-[#9FB0C7]">🎁 Пісня для вас</p>
                  <h1 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold">
                    {data.tracks[0]?.title || "Пісня"}
                  </h1>
                </>
              ) : (
                <>
                  <h1 className="mt-4 font-display text-2xl sm:text-3xl font-extrabold uppercase">Ваша пісня готова!</h1>
                  {data.number && (
                    <p className="mt-1 text-sm text-[#9FB0C7] font-mono">Замовлення #{data.number}</p>
                  )}
                </>
              )}
            </div>

            {(generating || pendingRegen) && !data.gift && !redoActive && (
              <div data-testid="redo-generating"
                className="mt-5 flex items-center justify-center gap-2.5 rounded-2xl border border-[#2BA8FF]/30 bg-[#2BA8FF]/10 px-4 py-3 text-sm text-[#8CCBFF]">
                <Loader2 size={16} className="animate-spin shrink-0" />
                Генерується новий варіант… Зазвичай це кілька хвилин.
              </div>
            )}

            {!data.gift && redoActive && (
              <div data-testid="redo-status"
                className={`mt-5 rounded-2xl border px-4 py-3.5 ${
                  data.redo.status_group === "generating"
                    ? "border-[#2BA8FF]/30 bg-[#2BA8FF]/10"
                    : "border-[#FFD400]/25 bg-[#FFD400]/[0.08]"}`}>
                <div className="flex items-center gap-3">
                  <div className={`grid place-items-center w-10 h-10 rounded-full shrink-0 ${
                    data.redo.status_group === "generating" ? "bg-[#2BA8FF]/20" : "bg-[#FFD400]/15"}`}>
                    {data.redo.status_group === "generating"
                      ? <Loader2 size={18} className="animate-spin text-[#8CCBFF]" />
                      : <Clock size={18} className="text-[#FFD400]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`font-display font-extrabold uppercase text-sm ${
                      data.redo.status_group === "generating" ? "text-[#8CCBFF]" : "text-[#FFD400]"}`}>
                      {data.redo.status_label}
                    </div>
                    <div className="text-xs text-[#9FB0C7] mt-0.5">
                      Нова пісня з'явиться тут разом зі старими
                    </div>
                  </div>
                  {data.redo.number && (
                    <span className="font-mono text-[11px] text-[#647089] shrink-0">#{data.redo.number}</span>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 space-y-3">
              {data.tracks.map((t) => (
                <TrackPlayer key={`main-${t.idx}`} track={t} badge={data.gift ? "" : topBadge} hideVariant={!!data.gift}
                  onEditTitle={data.gift ? undefined : setRenameTarget}
                  downloadUrl={`${API}/public/share/${code}/tracks/${t.idx}/download`}
                  onShare={() => setShareTrack(t)} />
              ))}
            </div>

            {!data.gift && newRuns.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="font-display font-extrabold uppercase text-sm">Нові варіанти</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <div className="space-y-3">
                  {newRuns.map((run) => (run.tracks || []).map((t) => (
                    <TrackPlayer key={`new-${run.run_id}-${t.idx}`} track={t} badge="Нова" hideVariant
                      onEditTitle={setRenameTarget}
                      downloadUrl={t.url}
                      onShare={() => shareDirect(t)} />
                  )))}
                </div>
              </div>
            )}

            {!data.gift && prevRuns.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="font-display font-extrabold uppercase text-sm text-[#9FB0C7]">Попередні варіанти</span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <div className="space-y-3">
                  {prevRuns.map((run) => (run.tracks || []).map((t) => (
                    <TrackPlayer key={`prev-${run.run_id}-${t.idx}`} track={t} hideVariant
                      onEditTitle={setRenameTarget}
                      downloadUrl={t.url}
                      onShare={() => shareDirect(t)} />
                  )))}
                </div>
              </div>
            )}

            {!data.gift && (
              <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-2.5">
                <button data-testid="redo-open" onClick={() => setRedoOpen(true)}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/12 bg-white/[0.05] backdrop-blur-md px-5 py-2.5 text-sm font-bold text-[#C6D3E6] hover:border-[#2BA8FF]/50 hover:text-white transition-colors">
                  <RefreshCw size={15} className="text-[#2BA8FF]" /> Переробити
                </button>
                <button onClick={copyPageLink}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-2.5 text-sm text-[#8CCBFF] hover:text-white transition-colors">
                  <Link2 size={14} /> Скопіювати посилання на сторінку
                </button>
              </div>
            )}

            <p className="mt-8 text-center text-xs text-[#647089]">Створено з ❤️ на E-PISNYA</p>
          </>
        )}
      </div>

      {renameTarget && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[#060A14]/70 backdrop-blur-sm" onClick={() => setRenameTarget(null)} />
          <div className="relative w-full max-w-sm rounded-3xl border border-white/12 bg-[#0B1220] p-5 shadow-2xl">
            <div className="flex items-center justify-between gap-3">
              <div className="font-display font-extrabold uppercase text-lg">Назва пісні</div>
              <button onClick={() => setRenameTarget(null)} aria-label="Закрити"
                className="grid place-items-center w-9 h-9 rounded-full border border-white/10 text-[#9FB0C7] hover:text-white hover:border-white/30 transition-colors">
                <X size={17} />
              </button>
            </div>
            <input data-testid="track-rename-input" value={renameValue} maxLength={80} autoFocus
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveRename(); if (e.key === "Escape") setRenameTarget(null); }}
              className="mt-4 w-full rounded-2xl bg-[#0B1220] border border-white/12 focus:border-[#2BA8FF] outline-none px-4 py-3 text-base font-bold" />
            <button onClick={saveRename} disabled={renameSaving || !renameValue.trim()} data-testid="track-rename-save"
              className="mt-4 w-full btn-tg grad-tg text-white font-bold px-6 py-3.5 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-40">
              {renameSaving ? <Loader2 size={16} className="animate-spin" /> : null} Зберегти
            </button>
          </div>
        </div>
      )}

      {redoOpen && data && !data.gift && (
        <RedoModal code={code} data={data}
          onClose={() => setRedoOpen(false)}
          onRegenStarted={() => {
            setRedoOpen(false);
            setGenerating(true);
            setPendingRegen(true);
            toast.success("Перегенерацію запущено ✓");
          }}
          onFreeUsed={() => setData((prev) => (prev ? { ...prev, regen_free_used: true } : prev))}
          onOrderSent={() => {
            setPendingRegen(true);
            api.get(`/public/share/${code}`).then((r) => setData(r.data)).catch(() => {});
          }} />
      )}

      {shareTrack && (
        <div className="fixed inset-0 z-[90] bg-[#060A14]/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0"
          onClick={() => { if (!busyShare) setShareTrack(null); }}>
          <div className="w-full max-w-sm rounded-3xl border border-white/12 bg-white/[0.08] backdrop-blur-2xl p-5"
            onClick={(e) => e.stopPropagation()}>
            <div className="font-display font-extrabold uppercase text-sm truncate">
              Надіслати «{shareTrack.title}»
            </div>
            <div className="mt-4 space-y-2.5">
              <button onClick={shareFile} disabled={!!busyShare}
                className="w-full flex items-center gap-3 rounded-2xl border border-white/12 bg-white/[0.05] px-4 py-3.5 hover:bg-white/[0.1] disabled:opacity-50 text-left transition-colors">
                <span className="text-xl shrink-0">📎</span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold">
                    {busyShare === "file" ? "Готую файл…" : "Надіслати файлом"}
                  </span>
                  <span className="block text-[11px] text-[#9FB0C7]">Просто музика (mp3) — у будь-який месенджер</span>
                </span>
              </button>
              <button onClick={shareLink} disabled={!!busyShare}
                className="w-full flex items-center gap-3 rounded-2xl border border-white/12 bg-white/[0.05] px-4 py-3.5 hover:bg-white/[0.1] disabled:opacity-50 text-left transition-colors">
                <span className="text-xl shrink-0">🔗</span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold">
                    {busyShare === "link" ? "Створюю посилання…" : "Надіслати посиланням"}
                  </span>
                  <span className="block text-[11px] text-[#9FB0C7]">
                    Сторінка-подарунок: назва пісні + плеєр, без номера замовлення
                  </span>
                </span>
              </button>
            </div>
            <button onClick={() => setShareTrack(null)} disabled={!!busyShare}
              className="mt-3 w-full text-center text-xs text-[#647089] hover:text-white transition-colors disabled:opacity-50">
              Скасувати
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
